import React from 'react';
import {
  Expressions,
  matchExpression,
  withSystem,
  Component,
  Plugins,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {TListOfElement, TRecord, TText} from '@packages/systems/wfdl-ast';
import {namesEqual, qualify} from '@packages/systems/wfdl-Qualified';
import {constantFalse} from '@packages/utilities/fp/utils';
import {ERROR_STATE} from '@packages/systems/users/constants';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const ERROR_STATE_COMPONENT_TYPE = {
  [ERROR_STATE.LOGIN]: qualify<Component.ComponentName>(
    Plugins.name('Users'),
    Component.name('UserLogInErrorMsg')
  ),
  [ERROR_STATE.SIGNUP]: qualify<Component.ComponentName>(
    Plugins.name('Users'),
    Component.name('UserSignUpErrorMsg')
  ),
  [ERROR_STATE.UPDATE_PASSWORD]: qualify<Component.ComponentName>(
    Plugins.name('Users'),
    Component.name('UserUpdatePasswordErrorMsg')
  ),
  [ERROR_STATE.RESET_PASSWORD]: qualify<Component.ComponentName>(
    Plugins.name('Users'),
    Component.name('UserResetPasswordErrorMsg')
  ),
} as const;

const DEFAULT_ERROR_COMPONENT_TYPE = qualify<Component.ComponentName>(
  Plugins.name('Users'),
  Component.name('UserErrorMsg')
);

const USER_TYPE_PATH = [
  {in: 'Record', at: 'users'},
  {in: 'Record', at: 'type'},
] satisfies ExpressionPath;

const UserFormErrorStateChildrenType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields: {
    users: TRecord({
      type: withSystem({private: true}, TText),
    }),
    children: TListOfElement,
  },
  render: function RenderCheckboxChildren({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    const strippedExpr = stripAll(expr);
    const errorTypeExpr = Expressions.getIn(strippedExpr, USER_TYPE_PATH);

    if (errorTypeExpr === undefined) {
      console.error(
        'errorTypeExpr of UserFormErrorState should not be undefined'
      );
      return null;
    }
    const errorType = Expressions.getText(errorTypeExpr);

    if (errorType === null) {
      console.error('errorType of UserFormErrorState should not be null');
      return null;
    }

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly "login-error-state": Qualified<string>; readonly "signup-error-state": Qualified<string>; readonly "update-password-error-state": Qualified<string>; readonly "reset-password-error-state": Qualified<...>; }'.
    const errorComponent = ERROR_STATE_COMPONENT_TYPE[errorType];

    const errorComponentPath = Expressions.findPaths(
      strippedExpr,
      matchExpression({
        Element: (el) =>
          errorComponent
            ? namesEqual(el.type, errorComponent)
            : namesEqual(el.type, DEFAULT_ERROR_COMPONENT_TYPE),
        _: constantFalse,
      })
    )[0];

    if (errorComponentPath === undefined) {
      console.error('no matching element in error component found');
      return null;
    }

    const path = [
      ...errorComponentPath,
      {in: 'Element'},
    ] satisfies ExpressionPath;
    const errorData = getIn(expr, path);
    if (errorData === undefined) {
      console.error('errorData of UserFormErrorState should not be undefined');
      return null;
    }

    const onErrorChange = extendExpressionEditorActionAtPath(dispatch)(path);

    return <ExpressionEditor expression={errorData} dispatch={onErrorChange} />;
  },
});

export default UserFormErrorStateChildrenType;
